/**
 * Power Zone Calculator
 *
 * Calculates power zones from FTP using a specified method.
 */

import { findMethod, POWER_METHODS } from "../lib/zone-methods";
import type { PowerZone } from "../types/profile";

/**
 * Calculate power zones using specified method
 *
 * @param methodId - Zone method ID (defaults to "coggan-7")
 * @returns Array of power zones with FTP percentage ranges
 */
export const calculatePowerZones = (
  methodId = "coggan-7"
): Array<PowerZone> => {
  const method = findMethod(POWER_METHODS, methodId);
  if (!method) return [];

  return method.defaults.map((def, i) => ({
    zone: i + 1,
    name: def.name,
    minPercent: def.minPercent,
    maxPercent: def.maxPercent,
  }));
};

/**
 * Calculate power zones with absolute watt values
 */
export const calculatePowerZoneValues = (
  ftp: number,
  methodId = "coggan-7"
): Array<{
  zone: number;
  name: string;
  minWatts: number;
  maxWatts: number;
}> => {
  const zones = calculatePowerZones(methodId);
  const result: Array<{
    zone: number;
    name: string;
    minWatts: number;
    maxWatts: number;
  }> = [];
  for (let i = 0; i < zones.length; i++) {
    const z = zones[i]!;
    const maxWatts = Math.round((ftp * z.maxPercent) / 100);
    const prev = result[i - 1];
    const minWatts =
      i === 0 || prev === undefined
        ? Math.round((ftp * z.minPercent) / 100)
        : prev.maxWatts + 1;
    result.push({ zone: z.zone, name: z.name, minWatts, maxWatts });
  }
  return result;
};
